import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  LuFacebook,
  LuGithub,
  LuInstagram,
  LuLinkedin,
  LuMail,
  LuMapPin,
  LuPhone,
} from "react-icons/lu";
import { legalPages, sectionPath } from "../routes";
import { Container } from "./ui";

const exploreLinks = [
  { label: "Services", id: "services" },
  { label: "AI & Data", id: "ai-data" },
  { label: "Work", id: "portfolio" },
  { label: "About", id: "about" },
  { label: "Tech stack", id: "skills" },
  { label: "Contact", id: "contact" },
];

const serviceLinks = [
  "AI, ML & Deep Learning",
  "Web Development",
  "Mobile Development",
  "IoT Integration",
  "DevOps Consulting",
];

const socialLinks = [
  {
    icon: <LuLinkedin />,
    href: "https://www.linkedin.com/company/code-solutions1",
    label: "LinkedIn",
  },
  { icon: <LuGithub />, href: "https://github.com/ussef11", label: "GitHub" },
  {
    icon: <LuFacebook />,
    href: "https://web.facebook.com/people/Code-Solutions/61582424098626/",
    label: "Facebook",
  },
  {
    icon: <LuInstagram />,
    href: "https://instagram.com/codesolutions0",
    label: "Instagram",
  },
];

const FooterWrap = styled.footer`
  background: var(--color-bg);
  border-top: 1px solid var(--color-border);
`;

const Top = styled(Container)`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) repeat(3, minmax(0, 1fr));
  gap: 48px;
  padding-top: 72px;
  padding-bottom: 56px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding-top: 56px;
  }
`;

const Brand = styled.div`
  > a {
    display: inline-block;
  }

  img {
    height: 34px;
    width: auto;
    mix-blend-mode: multiply;
    margin-bottom: 20px;
  }

  p {
    max-width: 340px;
    font-size: 0.9375rem;
    color: var(--color-muted);
  }

  @media (max-width: 960px) {
    grid-column: 1 / -1;
  }
`;

const Column = styled.nav`
  h2 {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-muted);
    margin-bottom: 16px;
  }

  ul {
    display: grid;
    gap: 10px;
  }

  a,
  li > span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9375rem;
    color: var(--color-text);
    transition: color 0.2s ease;
  }

  a:hover {
    color: var(--color-primary);
  }

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: var(--color-muted);
  }
`;

const Socials = styled.ul`
  display: flex;
  gap: 8px;
  margin-top: 24px;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  transition: border-color 0.2s ease, color 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    border-color: var(--color-ink);
    color: var(--color-ink);
  }
`;

const Bottom = styled.div`
  border-top: 1px solid var(--color-border);

  ${Container} {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 24px;
    padding-top: 24px;
    /* Clear the floating chat and back-to-top buttons */
    padding-bottom: 96px;
    font-size: 0.875rem;
    color: var(--color-muted);
  }
`;

const LegalLinks = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;

  a {
    display: inline-block;
    padding: 4px 0;
    color: var(--color-muted);
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-ink);
    }
  }
`;

const Footer = () => (
  <FooterWrap>
    <Top>
      <Brand>
        <Link to="/" aria-label="Code Solutions — home">
          <img src="/LOGO.png" alt="Code Solutions" width="105" height="34" loading="lazy" />
        </Link>
        <p>
          Full-stack web &amp; mobile development team specializing in AI/ML,
          IoT integration and DevOps consulting. Let's build something great
          together.
        </p>
        <Socials>
          {socialLinks.map(social => (
            <li key={social.label}>
              <SocialLink
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
              >
                {social.icon}
              </SocialLink>
            </li>
          ))}
        </Socials>
      </Brand>

      <Column aria-labelledby="footer-explore">
        <h2 id="footer-explore">Explore</h2>
        <ul>
          {exploreLinks.map(link => (
            <li key={link.id}>
              <Link to={sectionPath(link.id)}>{link.label}</Link>
            </li>
          ))}
        </ul>
      </Column>

      <Column aria-labelledby="footer-services">
        <h2 id="footer-services">Services</h2>
        <ul>
          {serviceLinks.map(label => (
            <li key={label}>
              <Link to={sectionPath("services")}>{label}</Link>
            </li>
          ))}
        </ul>
      </Column>

      <Column aria-labelledby="footer-contact">
        <h2 id="footer-contact">Contact</h2>
        <ul>
          <li>
            <a href="mailto:ussefbelkhiraoui@gmail.com">
              <LuMail aria-hidden="true" />
              Email us
            </a>
          </li>
          <li>
            <a href="tel:+212660577967">
              <LuPhone aria-hidden="true" />
              +212 660-577967
            </a>
          </li>
          <li>
            <span>
              <LuMapPin aria-hidden="true" />
              Rabat, Morocco
            </span>
          </li>
        </ul>
      </Column>
    </Top>

    <Bottom>
      <Container>
        <span>© {new Date().getFullYear()} Code Solutions. All rights reserved.</span>
        <nav aria-label="Legal">
          <LegalLinks>
            {legalPages.map(page => (
              <li key={page.path}>
                <Link to={page.path}>{page.label}</Link>
              </li>
            ))}
          </LegalLinks>
        </nav>
      </Container>
    </Bottom>
  </FooterWrap>
);

export default Footer;
